import { FC, PropsWithChildren, useEffect, useMemo, useRef } from 'react';

import { PlayerRef } from '@remotion/player';
import { useAtomValue } from 'jotai';
import { throttle } from 'lodash';

import { currentFrameAtom } from '@store';

import { FPS } from './VideoCell';

type VideoScrubProps = {
    playerRef: React.RefObject<PlayerRef | null>;
};

export const VideoScrub: FC<PropsWithChildren<VideoScrubProps>> = ({ children, playerRef }) => {
    const isPlayingRef = useRef(false);
    const isSeekingRef = useRef(false);
    const seekingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const currentFrame = useAtomValue(currentFrameAtom);

    const throttledSeek = useMemo(
        () =>
            throttle((frame: number) => {
                const currentFrame = playerRef.current?.getCurrentFrame();
                if (frame === currentFrame) {
                    return;
                }
                playerRef.current?.seekTo(frame);
                playerRef.current?.dispatchFrameUpdate({ frame });
            }, 1000 / FPS.video),
        [playerRef],
    );

    useEffect(() => {
        /**
         * When the video is playing, the inner frame is synced to progress by the player.
         * We don't want the progress to sync to the player at that time, because it would result in an endless loop.
         */
        const player = playerRef?.current;
        if (!player) return;

        const onPlay = () => {
            isPlayingRef.current = true;
        };
        const onPause = () => {
            isPlayingRef.current = false;
        };

        const onSeeked = () => {
            isSeekingRef.current = true;
            const previousTimeout = seekingTimeoutRef.current;
            if (previousTimeout) {
                clearTimeout(previousTimeout);
            }
            seekingTimeoutRef.current = setTimeout(() => {
                isSeekingRef.current = false;
            }, 150);
        };

        player.addEventListener('play', onPlay);
        player.addEventListener('pause', onPause);
        player.addEventListener('seeked', onSeeked);

        return () => {
            player.removeEventListener('play', onPlay);
            player.removeEventListener('pause', onPause);
            player.removeEventListener('seeked', onSeeked);
            const timeout = seekingTimeoutRef.current;
            if (timeout) {
                clearTimeout(timeout);
            }
        };
    }, [playerRef]);

    useEffect(() => {
        if (isPlayingRef.current) {
            return;
        }
        throttledSeek(Math.round(currentFrame));
    }, [currentFrame, throttledSeek]);

    return children;
};
